import { useState, useEffect, useMemo, useCallback } from "react";
import {
    createReport,
    getReportDownloadUrl,
    listReports,
} from "@/services/reportingService";
import type { ReportSummary } from "@/old/document/types/api";
import { Button } from "@/components/ui/button-new";
import IconButton from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Plus, Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePageFab } from "@/hooks/use-page-fab";
import { format as dateFnsFormat } from "date-fns";
import { toast } from "sonner";
import { formatFileSize } from "@/lib/utils";
import { H1HeaderCard } from "@/components/layout/h1-header-card";

// Safe date formatting function
const formatDate = (
    dateValue: string | undefined | null,
    formatString: string,
    fallback: string = "Invalid date",
): string => {
    if (!dateValue) return fallback;
    try {
        return dateFnsFormat(new Date(dateValue), formatString);
    } catch (e) {
        console.error(`Invalid date format: ${dateValue}`, e);
        return fallback;
    }
};

// Check if report is incomplete (created before period end)
const getToDateIndicator = (
    createdAt: string,
    reportYear: string,
    reportMonth?: string,
    period?: string,
): string | null => {
    if (!createdAt || !reportYear) return null;
    try {
        const createdDate = new Date(createdAt);

        if (period === "MONTHLY" && reportMonth) {
            // For monthly reports, check if created before month end
            const monthEnd = new Date(`${reportYear}-${reportMonth}-01`);
            monthEnd.setMonth(monthEnd.getMonth() + 1);
            monthEnd.setDate(0); // Last day of the month
            return createdDate < monthEnd ? "MONTH TO DATE" : null;
        } else {
            // For annual reports, check if created before year end
            const yearEnd = new Date(`${reportYear}-12-31`);
            return createdDate < yearEnd ? "YEAR TO DATE" : null;
        }
    } catch {
        return null;
    }
};

const ReportsPage = () => {
    const [reports, setReports] = useState<ReportSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear().toString());
    const [month, setMonth] = useState<string>("");
    const [period, setPeriod] = useState<"MONTHLY" | "ANNUAL">("ANNUAL");
    const [format, setFormat] = useState<"CSV" | "ARCHIVE">("CSV");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const loadReports = async () => {
        try {
            setLoading(true);
            const response = await listReports();
            setReports(response.reports);
        } catch (error) {
            console.error("Failed to load reports:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateReport = async () => {
        try {
            setLoading(true);
            await createReport({
                year,
                month: period === "MONTHLY" ? month : undefined,
                period,
                format,
                reportType: "EXPENSES",
            });
            const periodText = period === "MONTHLY" ? `${month}/${year}` : year;
            toast.success(
                `${format} report for ${periodText} created successfully`,
            );
            await loadReports();
        } catch (error: any) {
            console.error("Failed to create report:", error);

            // Extract error message from various possible error structures
            let errorMessage = "Failed to create report. Please try again.";
            let nextAllowedTime = null;

            if (error.response?.status === 429) {
                const errorData = error.response.data;
                errorMessage =
                    errorData.message || "Report generation is rate limited";
                nextAllowedTime = errorData.nextAllowedTime;
            } else if (error.message) {
                // Handle cases like "UnknownError: Report generation is rate limited..."
                errorMessage = error.message.replace(/^UnknownError:\s*/, "");
            }

            toast.error(errorMessage, {
                duration: 5000,
                description: nextAllowedTime
                    ? `Next allowed: ${new Date(nextAllowedTime).toLocaleString()}`
                    : undefined,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (reportId: string) => {
        try {
            const response = await getReportDownloadUrl(reportId);
            // window.open(response.downloadUrl, "_blank"); will be blocked as popup
            window.location.href = response.downloadUrl;
            toast.success("Report download started");
        } catch (error) {
            console.error("Failed to get download URL:", error);
            toast.error("Failed to download report. Please try again.");
        }
    };

    useEffect(() => {
        loadReports();
    }, []);

    // FAB implementation
    const handleFabClick = useCallback(() => {
        // Reset form to defaults
        setYear(new Date().getFullYear().toString());
        setMonth("");
        setPeriod("ANNUAL");
        setFormat("CSV");
        setIsCreateModalOpen(true);
    }, []);

    const fabAction = useMemo(
        () => ({
            icon: Plus,
            label: "Create Report",
            onClick: handleFabClick,
            color: "primary" as const,
        }),
        [handleFabClick],
    );

    usePageFab(fabAction);

    const getStatusColor = (status: string) => {
        switch (status) {
            case "READY":
                return "bg-green-100 text-green-800 border border-green-400";
            case "FAILED":
                return "bg-red-100 text-red-800 border border-red-400";
            case "PROCESSING":
                return "bg-amber-100 text-amber-800 border border-amber-400";
            default:
                return "bg-gray-100 text-gray-800 border border-gray-400";
        }
    };

    return (
        <div className="container max-w-xl mx-auto space-y-4">
            {/* Create Report Modal */}
            <Dialog
                open={isCreateModalOpen}
                onOpenChange={(open) => {
                    setIsCreateModalOpen(open);
                    if (!open) {
                        // Reset form when closing
                        setYear(new Date().getFullYear().toString());
                        setMonth("");
                        setPeriod("ANNUAL");
                        setFormat("CSV");
                    }
                }}
            >
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Create New Report</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-md-on-surface">
                                    Period
                                </Label>
                                <Select
                                    value={period}
                                    onValueChange={(
                                        value: "MONTHLY" | "ANNUAL",
                                    ) => setPeriod(value)}
                                >
                                    <SelectTrigger size="sm" className="w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ANNUAL">
                                            Annual
                                        </SelectItem>
                                        <SelectItem value="MONTHLY">
                                            Monthly
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-md-on-surface">
                                    Format
                                </Label>
                                <Select
                                    value={format}
                                    onValueChange={(value: "CSV" | "ARCHIVE") =>
                                        setFormat(value)
                                    }
                                >
                                    <SelectTrigger size="sm" className="w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="CSV">CSV</SelectItem>
                                        <SelectItem value="ARCHIVE">
                                            Archive
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label
                                    htmlFor="year"
                                    className="text-sm font-medium text-md-on-surface"
                                >
                                    Year
                                </Label>
                                <Input
                                    id="year"
                                    value={year}
                                    onChange={(e) => setYear(e.target.value)}
                                    className="h-9 w-full"
                                    placeholder="2024"
                                />
                            </div>
                            {period === "MONTHLY" && (
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="month"
                                        className="text-sm font-medium text-md-on-surface"
                                    >
                                        Month
                                    </Label>
                                    <Select
                                        value={month}
                                        onValueChange={setMonth}
                                    >
                                        <SelectTrigger
                                            size="sm"
                                            className="w-full"
                                        >
                                            <SelectValue placeholder="Select month" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="01">
                                                January
                                            </SelectItem>
                                            <SelectItem value="02">
                                                February
                                            </SelectItem>
                                            <SelectItem value="03">
                                                March
                                            </SelectItem>
                                            <SelectItem value="04">
                                                April
                                            </SelectItem>
                                            <SelectItem value="05">
                                                May
                                            </SelectItem>
                                            <SelectItem value="06">
                                                June
                                            </SelectItem>
                                            <SelectItem value="07">
                                                July
                                            </SelectItem>
                                            <SelectItem value="08">
                                                August
                                            </SelectItem>
                                            <SelectItem value="09">
                                                September
                                            </SelectItem>
                                            <SelectItem value="10">
                                                October
                                            </SelectItem>
                                            <SelectItem value="11">
                                                November
                                            </SelectItem>
                                            <SelectItem value="12">
                                                December
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" size="sm">
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            onClick={() => {
                                handleCreateReport();
                                setIsCreateModalOpen(false);
                            }}
                            disabled={
                                loading || (period === "MONTHLY" && !month)
                            }
                            size="sm"
                            loading={loading}
                        >
                            {loading ? "Creating..." : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reports List */}

            <H1HeaderCard title="Reports">
                <p className="text-sm text-md-on-surface-variant leading-relaxed tracking-wide mt-1">
                    Generate monthly or annual reports as CSV or archive format.
                    Archives include both CSV data and original documents.
                </p>
                <p>&nbsp;</p>
                <p className="text-sm text-md-on-surface-variant leading-relaxed tracking-wide mt-1">
                    Reports reflect data at creation time, and only include
                    approved documents.
                </p>
            </H1HeaderCard>

            <div className="flex items-end justify-end gap-8 mt-14">
                {/* Header */}
                {/* <div className="">
                    <h1 className="text-2xl font-semibold text-md-on-surface leading-tight tracking-tight">
                        Reports
                    </h1>
                    <p className="text-sm text-md-on-surface-variant leading-relaxed tracking-wide mt-1">
                        Generate monthly or annual reports as CSV or archive
                        format. Archives include both CSV data and original
                        documents.
                    </p>
                    <p className="text-sm text-md-on-surface-variant leading-relaxed tracking-wide mt-1">
                        Reports reflect data at creation time, and only include
                        approved documents.
                    </p>
                </div> */}
                <IconButton
                    onClick={loadReports}
                    disabled={loading}
                    size="sm"
                    color="secondary"
                    variant="outlined"
                >
                    <RefreshCw
                        className={cn("h-4 w-4", loading && "animate-spin")}
                    />
                </IconButton>
            </div>

            {loading && reports.length === 0 && (
                <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-2 text-md-on-surface-variant">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Loading reports...</span>
                    </div>
                </div>
            )}

            {reports.length === 0 && !loading && (
                <div className="text-center py-12">
                    <div className="p-3 bg-md-surface-variant rounded-full w-fit mx-auto mb-3">
                        <FileText className="h-6 w-6 text-md-on-surface-variant" />
                    </div>
                    <p className="text-md-on-surface-variant text-sm">
                        No reports found
                    </p>
                    <p className="text-md-on-surface-variant text-xs mt-1">
                        Create your first report to get started
                    </p>
                </div>
            )}

            <div className="space-y-2">
                {reports.map((report) => (
                    <Card
                        key={report.reportId}
                        className="bg-md-surface-level-1 border-md-outline-variant p-0"
                    >
                        <CardContent className="p-4">
                            <div className="flex justify-between items-center">
                                <div className="space-y-2 flex-1">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-medium text-md-on-surface">
                                            {report.reportType} Report &mdash;{" "}
                                            {report.period === "MONTHLY" &&
                                            report.month
                                                ? `${report.month}/${report.year}`
                                                : report.year}{" "}
                                            ({report.format})
                                        </h3>
                                        <div
                                            className={cn(
                                                "px-2 py-1 rounded-full text-xs font-medium",
                                                getStatusColor(report.status),
                                            )}
                                        >
                                            {report.status}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-start gap-1 text-xs text-md-on-surface-variant">
                                        <div className="flex items-center gap-3">
                                            <span>
                                                Created{" "}
                                                {formatDate(
                                                    report.createdAt,
                                                    `yyyy-MM-dd ${new Intl.DateTimeFormat().resolvedOptions().hour12 ? "hh:mm a" : "HH:mm"}`,
                                                )}
                                            </span>
                                            {report.period && (
                                                <span className="px-1.5 py-0.5 bg-md-surface-variant rounded text-xs">
                                                    {report.period}
                                                </span>
                                            )}
                                            {getToDateIndicator(
                                                report.createdAt,
                                                report.year,
                                                report.month,
                                                report.period,
                                            ) && (
                                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-400 rounded text-xs">
                                                    {getToDateIndicator(
                                                        report.createdAt,
                                                        report.year,
                                                        report.month,
                                                        report.period,
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-row gap-2">
                                            {report.recordCount > 0 && (
                                                <span>
                                                    {report.recordCount} records
                                                </span>
                                            )}
                                            {report.fileSize && (
                                                <span>
                                                    {formatFileSize(
                                                        report.fileSize,
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-1 ml-4">
                                    {report.status === "READY" && (
                                        <IconButton
                                            onClick={() =>
                                                handleDownload(report.reportId)
                                            }
                                            size="sm"
                                            color="primary"
                                            variant="filled"
                                        >
                                            <Download className="h-4 w-4" />
                                        </IconButton>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default ReportsPage;
